import { SectionsProvider } from '@/content/providers/sections.provider';
import { COURSE_EVENTS } from '@/content/values/events';
import { SECTION_TYPES } from '@/content/values/sections';
import { EduacProvider } from '@/core/providers/eduac.provider';
import { Section, SectionSubmissionDates } from '@/courses/models/section.model';
import { COMPONENT_TYPE } from '@/courses/values/courses.values';
import { FixedSection } from '../types/sections.types';

class SectionProviderClass extends EduacProvider {

  getFolderIcons(fixedSection: FixedSection): { closed?: string, open?: string } {
    switch(fixedSection) {
      case 'LESSON':
        return { closed: 'folder-solid', open: 'folder-open-solid' };
      case 'CARDS_DECK':
        return { closed: 'deck', open: 'deck' };
      default:
        return {}      
    }
  }

  getTypeFromExtension(extension: string = '') {
    let keys = Object.keys(SECTION_TYPES);
    let type = '';
    keys.forEach((key) => {
      const extensions = SECTION_TYPES[key].extensions;
      if(extensions) {
        let extFound = extensions.find(ext => ext == extension.toUpperCase());
        if(extFound && SECTION_TYPES[key].groupName == 'file') {
          type = SECTION_TYPES[key].code;
        }
      }
    });
    return type;
  }

  getDueDateInfo(form, defaultDueDate, defaultLastDate, edition, section: Section, submissionDates: SectionSubmissionDates) {
    let res = { dueDateAuto: true, lastDateAuto: true };
    if(form) {
      const formHasDates = form.dueDate || form.lastDate;
      if(defaultDueDate && defaultLastDate && formHasDates) {
        const resByDefault = () => {
          res.dueDateAuto = form.dueDate.value === defaultDueDate;
          res.lastDateAuto = form.lastDate.value === defaultLastDate;
        }
        if(!edition) {
          resByDefault();
        } else {
          let dates: SectionSubmissionDates = section ? section.submissionDates : submissionDates;
          if(dates) {
            let isTheSameDueDate = dates.dueDate === form.dueDate.value;
            let isTheSameLastDate = dates.lastDate === form.dueDate.value;
            res.dueDateAuto = isTheSameDueDate && dates.dueDateAuto ? true : form.dueDate.value === defaultDueDate;
            res.lastDateAuto = dates.lastDateAuto && isTheSameLastDate ? true : form.lastDate.value === defaultLastDate;
          } else {
            resByDefault();
          }
        }
      }
    }
    return res;
  }

  getHasSectionLessons(sections: Section[]): boolean {
    let hasLessons = false;
    for (let i = 0; i < sections.length; i++) {
      if(sections[i].fixedSection === 'LESSON') {
        hasLessons = true;
        break;
      } else if(sections[i].sections) {
        hasLessons = this.getHasSectionLessons(sections[i].sections);
      }
    }
    return hasLessons;
  }

  getHasParentLessons(sections: Section[], section: Section) {
    let hasLessons = false;
    let endRevision = false;
    let sectionToCheck = section;
    while(endRevision === false && hasLessons === false) {
      if(sectionToCheck.parentId) {
        const parent = SectionsProvider.getSectionByID(sections, sectionToCheck.parentId);
        if(parent) {
          hasLessons = parent.fixedSection === 'LESSON';
          if(!hasLessons) {
            sectionToCheck = parent;
          }
        } else {
          endRevision = true;
        }
      } else {
        endRevision = true;
      }
    }
    return hasLessons;
  }

  getLessonCounter(sections: Section[]): number {
    let count = 0;
    sections.forEach(s => {
      if(s.fixedSection === 'LESSON') {
        count++;
      }
      if(s.sections) {
        count += this.getLessonCounter(s.sections); 
      }
    });
    return count;
  }

  getIfFolderCanHaveObjects(sections: Section[]) {
    let canHaveObjects = true;
    for(let i = 0; i < sections.length; i++) {
      const isObject = sections[i].type !== SECTION_TYPES.SECTION.code;
      const isFolder = sections[i].type === SECTION_TYPES.SECTION.code && !sections[i].fixedSection;
      if(isObject) {
        canHaveObjects = false;
        break;
      }
      if(isFolder) {
        canHaveObjects = this.getIfFolderCanHaveObjects(sections[i].sections) && canHaveObjects;
      }
    }
    return canHaveObjects;
  }

  getIsTheLessonTreeDisordered(sections: Section[], nextSequence = 1) {
    let isDisordered = false;
    for(let i = 0; i < sections.length; i++) {
      if(SECTION_TYPES.SECTION.code === 'SECTION') {
        if(sections[i].fixedSection === 'LESSON') {
          if(sections[i].fixedSequence !== nextSequence) {
            isDisordered = true;
            break;
          }
          nextSequence++;
        } else {
          const res = this.getIsTheLessonTreeDisordered(sections[i].sections, nextSequence);
          nextSequence = res.nextSequence;
          isDisordered = res.isDisordered;
          if(isDisordered) {
            break;
          }
        }
      }
    }
    return { isDisordered, nextSequence };
  }

  confirmSectionMoveInTemplate(
    sectionToAdd: Section,
    prevIndex: number,
    productSections: Section[],
    currentParentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const { isDisordered } = SectionProvider.getIsTheLessonTreeDisordered(productSections);
      if (isDisordered) {
        this.$vue.$EdModal.createConfirmationModal({
          htmlBody: true,
          body: this.$vue.$i18n.t('sections.confirmMoveLessons'),
          firstLoadingIsDone: true,
          actions: [{
            label: this.$vue.$i18n.t('common.cancel'),
            mode: 'outline',
            action: async () => {
              this.$vue.$Courses.Store.revertSectionMoved({
                section: sectionToAdd,
                nextIndex:  prevIndex,
                currentParentId,
                nextParentId: sectionToAdd.parentId
              });
              reject({ modalUsed: true });
            }
          }, {
            label: this.$vue.$i18n.t('sections.move'),
            action: async () => {
              resolve({ modalUsed: true });
            }
          }]
        });
      } else {
        resolve({ modalUsed: false });
      }
    });
  }

  getNextLessonSequence(sections: Section[], sectionId: number) {
    let nextSequence = 0;
    for (let i = 0; i < sections.length; i++) {
      if (sections[i].fixedSection === 'LESSON') {
        nextSequence = sections[i].fixedSequence;
      } else {
        if (sections[i].sections && SECTION_TYPES.SECTION.code === 'SECTION') {
          const result = this.getNextLessonSequence(sections[i].sections, sectionId);
        
          nextSequence = result || nextSequence;
          if (sectionId === sections[i].id) {
            break;
          }
        }
      }
    }

    return nextSequence;
  }

  getContentSections(sections: Section[]) {
    return sections.filter(s => s.fixedSection !== 'FUN' && s.fixedSection !== 'FILLERS' && s.fixedSection !== 'ADDITIONAL');
  }

  openLessonCreator(
    contentId: number,
    classId: number = null,
    section: Section = null,
    minDate = '',
    isCreatingLesson = false,
    fixedSection: FixedSection = null) {
    this.$vue.$Courses.Store.setComponentSelected({
      courseId: contentId,
      classId, 
      componentInfo: { 
        name: COMPONENT_TYPE.LESSON_CREATOR.code,
        section,
        edition: false,
        minDate: minDate,
        isLesson: isCreatingLesson,
        fixedSection
      }
    });
    this.$vue.$edEventBus.$emit(COURSE_EVENTS.OBJECT_SELECTED, {
      materialId: null,
      sectionId: null,
      courseId: contentId, 
      classId
    });
  }

  openSubjectCalendar(contentId: number, classId: number) {
    this.$vue.$Courses.Store.setComponentSelected({
      courseId: contentId,
      classId,
      componentInfo: {
        name: COMPONENT_TYPE.ED_CONTENT_SUBJECT_CALENDAR.code,
        section: null,
        edition: false,
      }
    });
  }

  openDeckCreator(
    contentId: number,
    classId: number = null,
    section: Section = null,
    fixedSection: FixedSection = null) {
    this.$vue.$Courses.Store.setComponentSelected({
        courseId: contentId,
        classId,
        componentInfo: {
          name: COMPONENT_TYPE.DECK_CREATOR.code,
          section,
          edition: false,
          fixedSection
        }
      });
    this.$vue.$edEventBus.$emit(COURSE_EVENTS.OBJECT_SELECTED, {
      materialId: null,
      sectionId: null,
      courseId: contentId,
      classId
    });
  }

}

export const SectionProvider = new SectionProviderClass();